import { Alert } from "@mui/material";
import { Navigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  PaymentLoadingContent,
  RegistrationNotFound,
  RegistrationStepPage,
} from "components";
import { OnlinePaymentStatus } from "api";
import { PaymentCtaButton } from "./PaymentCtaButton";
import { ConfirmationNotStartedPayment } from "./ConfirmationNotStartedPayment";
import { ConfirmationSuccessPayment } from "./ConfirmationSuccessPayment";
import { useConfirmationQueries } from "./useConfirmationQueries";
import { ConfirmationFailedPayment } from "./ConfirmationFailedPayment";

export const Confirmation = () => {
  const { t } = useTranslation();
  const params = useParams<{ guid?: string }>();

  const {
    customerConfirmation,
    examDetails,
    paymentContext,
    isNotFound,
    isError,
    isFetching,
  } = useConfirmationQueries(params.guid);

  const paymentStatus = paymentContext?.paymentStatus;
  const paymentUrl = `/payment/${params.guid}`;

  const subTitle = (() => {
    switch (paymentStatus) {
      case OnlinePaymentStatus.Successful:
        return t("confirmation.success.title");
      case OnlinePaymentStatus.Failed:
      case OnlinePaymentStatus.Abandoned:
        return t("payment.failed.title");
      default:
        return t("confirmation.title");
    }
  })();

  const hideStepper =
    paymentStatus === OnlinePaymentStatus.Failed ||
    paymentStatus === OnlinePaymentStatus.Abandoned;

  if (isNotFound) {
    return <RegistrationNotFound />;
  }

  if (!params.guid || isError) {
    return <Alert severity="error">{t("common.generalError")}</Alert>;
  }

  if (!isFetching && paymentStatus === OnlinePaymentStatus.Initialized) {
    return <Navigate to={paymentUrl} replace />;
  }

  return (
    <RegistrationStepPage
      subTitle={subTitle}
      selectedExam={examDetails}
      completed
      backToHome
      hideStepper={hideStepper}
      examCardCta={<PaymentCtaButton paymentStatus={paymentStatus} />}
      paymentStatus={paymentStatus}
      examCardPlaceholder={isFetching}
      priceOverride={paymentContext?.amount}
      currencyOverride={paymentContext?.currency}
    >
      {isFetching ? (
        <PaymentLoadingContent
          icon="card"
          title={t("confirmation.loading.title")}
          subtitle={t("confirmation.loading.subtitle")}
          body={t("confirmation.loading.body")}
        />
      ) : (
        <>
          {paymentStatus === OnlinePaymentStatus.NotStarted && (
            <ConfirmationNotStartedPayment
              examDetails={examDetails}
              paymentDeadline={customerConfirmation?.paymentDeadline}
            />
          )}
          {(paymentStatus === OnlinePaymentStatus.Failed ||
            paymentStatus === OnlinePaymentStatus.Abandoned) && (
            <ConfirmationFailedPayment
              paymentDeadline={customerConfirmation?.paymentDeadline}
            />
          )}
          {paymentStatus === OnlinePaymentStatus.Successful && (
            <ConfirmationSuccessPayment examDetails={examDetails} />
          )}
        </>
      )}
    </RegistrationStepPage>
  );
};
